<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <form @submit.prevent="validateForm">
        <div class="row">
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4" v-if="itemsCompany">
              <label class="label text-secondary">Organizador</label>
              <select 
                class="form-select form-control h-45"
                aria-label="Default select example"
                v-model="tournament.companyId" 
                @change="clearError('companyId')"
              >
                <option :value="null">-- Seleccionar --</option>
                <option :value="company.id" v-for="(company, i) of itemsCompany"  :key="i" >{{company.name}}</option> 
              </select>
              <div class="input-error">{{ errors.companyId }}</div>
            </div>
          </div>
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Nombre</label>
              <input
                type="text"
                class="form-control h-45"
                placeholder="Ingresar el nombre del torneo"
                v-model="tournament.name"
                @input="clearError('name')"
              />
              <div v-if="errors.name"  class="input-error">{{ errors.name }}</div> 
            </div>
          </div>
        </div>
        <div class="row">
          
          <div class="col-lg-2 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Fecha Inicio</label>
              <VueDatePicker placeholder="Fecha y hora"
                  v-model="tournament.startDate" 
                  format="dd/MM/yyyy HH:mm" 
                  :format-locale="es" 
                  @input="clearError('startDate')" 
                  :minutes-increment="30"
                  :enable-time-picker="true"
                  is-24
                  teleport 
              />
              <div class="input-error">{{ errors.startDate }}</div>
            </div>
          </div>
          <div class="col-lg-2 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Fecha Fin</label>
              <VueDatePicker placeholder="Fecha y hora"
                v-model="tournament.endDate" 
                format="dd/MM/yyyy HH:mm" 
                :format-locale="es"
                @input="clearError('endDate')" 
                :minutes-increment="30" 
                is-24
                teleport 
              />
              <div class="input-error">{{ errors.endDate }}</div>
            </div>
          </div>
          <div class="col-lg-4 col-sm-6">
            <div class="form-group mb-4"  >
              <label class="label text-secondary">Título</label>
              <input
                type="text"
                class="form-control h-45"
                placeholder="Ingresar el título del torneo"
                v-model="tournament.title"
                @input="clearError('title')"
              />
              <div class="input-error">{{ errors.title }}</div>
            </div>
          </div>
        </div>
        <div class="row">
          
          <div class="col-lg-8 col-sm-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Descripción</label>
              <textarea  rows="4" class="form-control  text-dark no-resize"
                placeholder="Ingresar la descripción del torneo"
                v-model="tournament.description"
                @input="clearError('name')"
              ></textarea>
              <div v-if="errors.description"  class="input-error">{{ errors.description }}</div> 
            </div>
          </div>
        </div>
        <div class="row">

          <div class="col-lg-12">
            <div class="form-group mb-4">
              <label class="label text-secondary">Portada</label>
              <div v-if="tournament.urlCoverPhoto">
                <img  :src="tournament.urlCoverPhoto" alt="Portada de Torneo" class="img-fluid mb-2 d-block" />
                <button @click.prevent="removeCoverFile" class="btn btn-danger">Eliminar portada</button>
              </div>
              <FilePond
                name="fileCover"
                :maxFileSize="'2MB'"
                :accepted-file-types="supportedFormats" 
                allow-multiple="false"
                :max-files="1"
                label-idle="Arrastra tu imagen o <span class='filepond--label-action'>Buscar</span>"
                :labelMaxFileSizeExceeded="'El archivo es demasiado grande'"
                :labelMaxFileSize="'El tamaño máximo permitido es {filesize}'"
                :labelFileTypeNotAllowed="'El archivo tiene una extensión no permitida'" 
                :fileValidateTypeLabelExpectedTypes="`Los tipos de archivo permitidos son: ${supportedFormats.join(', ')}`"
                credits = ""
                @updatefiles="handleUpdateFiles"
                @input="clearError('fileCover')"
                v-if="!tournament.urlCoverPhoto"
              />
              <div class="input-error">{{ errors.fileCover }}</div>
            </div>
          </div>
          <div class="col-lg-12">
            <div class="d-flex flex-wrap gap-3">

              <RouterLink to="/tournaments" > 
                <button type="button" class="btn btn-danger bg-danger bg-opacity-10 text-danger border-0 fw-semibold py-2 px-4" >
                  Cancelar
                </button>
              </RouterLink>
              <button class="btn btn-primary bg-primary bg-opacity-10 text-primary border-0 fw-semibold py-2 px-4" type="submit" :disabled="isLoading" >
                  <span v-if="!isLoading">{{tournament.id == 0 ? 'Crear Torneo' : 'Actualizar Torneo'}}</span>
                  <span class="spinner-border spinner-border-sm me-2" aria-hidden="true" v-if="isLoading"></span>
                  <span role="status" v-if="isLoading">Procesando...</span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { tournamentSchema } from "@/schemas/tournamentSchema";
import { defineComponent, ref, onMounted } from "vue";
import api from '@/services/api';
import { useToast } from "vue-toastification";
import { useRouter, useRoute } from 'vue-router'
import moment from 'moment';

import vueFilePond from "vue-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type" ;
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import '@vuepic/vue-datepicker/dist/main.css';

import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
import { COMPANY_TYPES } from "@/utils/constants";
import { es } from 'date-fns/locale';

// Create FilePond component
const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview
);

export default defineComponent({
  name: "CreateTournament",
  components: {FilePond},

  setup() {
    const toast = useToast();
    const route = useRoute();
    const router = useRouter()
    const isLoading = ref(false);

    const itemsCompany = ref<any[]>([]);
    const tournament = ref({
        id: 0,
				name:null,
				companyId: null,
        startDate: null,
        endDate: null,
        title: null,
        description: null,
        fileCover: null,
        urlCoverPhoto: null
		}); 
    const errors = ref<Record<string, string>>({});

    const supportedFormats = ['image/jpg', 'image/jpeg', 'image/png'];

    onMounted(async () => {
      await getCompanies();
      var idTournament = route.params.id;
      if(idTournament){
        await getTournamentById(idTournament);
      }
    });

    const getCompanies = async() => {
        try {
          const response = await api.get(`/common/companies?type=${COMPANY_TYPES.PROMOTOR}`);
          itemsCompany.value = response.data;

        }catch(ex){
          console.error(ex);
        }
    };

    const handleUpdateFiles = (fileItems: any[]) => {
      tournament.value.fileCover = fileItems.length ? fileItems[0].file : null;
    };

    const validateForm = async () => {
    
      try {
        errors.value = {};
        await tournamentSchema.validate(tournament.value, { abortEarly: false });
        isLoading.value = true;
        
        await createTournament();
        isLoading.value = false;
      } catch (err: any) {
        console.log("err: " +err)
        if (err.inner) {
          err.inner.forEach((e: any) => {
            errors.value[e.path] = e.message;
          });
        }
        isLoading.value = false
      }
    };

    const clearError = (field: string) => {
      errors.value[field] = '';
    };
    
    const buildFormData = (data: any) => {
      const formData = new FormData();
      for (const key in data) {
        if (data[key] !== undefined && data[key] !== null && key != 'createdBy' ) {
          formData.append(key, data[key]);
        }
      }
      return formData;
    };

    const getTournamentById = async(id:any) => {
        try {
          const response = await api.get(`/tournament/${id}`);
          const responseFormat = response.data;
          responseFormat.startDate =  moment(responseFormat.startDate).format('YYYY-MM-DD HH:mm');
          responseFormat.endDate =  moment(responseFormat.endDate).format('YYYY-MM-DD HH:mm');

          if (responseFormat.urlCoverPhoto) {
            responseFormat.urlCoverPhoto = process.env.VUE_APP_API_URL + '/' + responseFormat.urlCoverPhoto;
          }
          tournament.value = responseFormat;
        
          
        }catch(ex){
          console.error(ex);
        }
    };

    const createTournament = async() => {
        try {
          const payload = {
            ...tournament.value,
            startDate: moment(tournament.value.startDate).toISOString(),
            endDate: moment(tournament.value.endDate).toISOString(),
          };

          const formData = buildFormData(payload);

          const response: any = await api.postForm(`/tournament`, formData);

          if(response.data.return){
            toast.success(`El torneo fue ${tournament.value.id == 0 ? "creado" : "actualizado" } con éxito.`, {
              timeout: 2000
            });

            router.push({name: "TournamentsPage"})
          }

        }catch(ex){
          toast.error("El torneo no pudo ser creado.", {
            timeout: 2000
          });
          console.error(ex);
        }
        
        
    };

    const removeCoverFile = async() => {
      tournament.value.urlCoverPhoto = null
    }

    return {
      handleUpdateFiles,
      createTournament,
      validateForm,
      clearError,
      removeCoverFile,
      tournament,
      itemsCompany,
      errors,
      isLoading,
      es,
      supportedFormats
    };
  }
});
</script>
